// Reception top bar — RON-5469. Prototype region: header.
//
// This screen opens in its OWN TAB and does not render the app's Sidebar, so this bar is the only
// chrome it has: who we are, which site, what time, and the way out.
//
// The bell (`#bell-trigger`, `#dd-notifications`) is deliberately NOT here. §9: "the bell's own
// feed has no endpoint yet", and the backend has asked us what a notification even is before they
// build one. A bell with invented contents on a reception desk is worse than no bell.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ronspotLogo from "../../../assets/images/ronspot-white-logo.svg";
import ReceptionSitePicker from "./ReceptionSitePicker.jsx";
import ReceptionUserMenu from "./ReceptionUserMenu.jsx";
import { getInitials } from "../receptionConstants.js";

const formatClockTime = (date) =>
  date.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit", hour12: false });

const formatClockDate = (date) =>
  date.toLocaleDateString("en-IE", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

// The signed-in employee, from the same localStorage keys the rest of the app reads.
const readUserName = () => {
  try {
    const first = localStorage.getItem("firstname") || "";
    const last = localStorage.getItem("lastname") || "";

    return `${first} ${last}`.trim();
  } catch {
    // Private mode or blocked storage. Not worth failing the bar over.
    return "";
  }
};

const ReceptionTopBar = ({
  sites,
  siteId,
  onSiteChange,
  onLock,
  canLock,
  counts,
  siteCounts,
  isLoadingSiteCounts,
  onSitePickerOpen,
}) => {
  const { t } = useTranslation("common");
  const [now, setNow] = useState(() => new Date());

  // A desk screen is watched all day, so the clock ticks rather than rendering once. Every 10s
  // rather than every 1s: the display has minute precision, so a per-second interval would be 59
  // re-renders an hour that change nothing.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10000);

    return () => clearInterval(timer);
  }, []);

  const site = sites.find((candidate) => candidate.id === siteId);
  const userName = readUserName();

  return (
    <header className="reception-topbar">
      {/* Vendored rather than hotlinked from
          `ronspot.s3.amazonaws.com/assets/images/logo/white/ronspot_white_logo.svg`.

          This screen runs unattended at a desk all day: a remote asset is one more thing that can
          be slow, blocked by a proxy, or simply unavailable, and a reception display with a hole
          where the brand should be is worse than one that loads from its own bundle. It is also
          how the Sidebar already treats its logos.

          The white variant is deliberate — every logo already in `assets/images` is the green
          #178477 one, which is unreadable on this bar's blue. */}
      <div className="reception-topbar__brand">
        <img className="reception-topbar__logo" src={ronspotLogo} alt="Ronspot" />

        {/* Two lines, as the prototype has it: the eyebrow says which product area, the line
            below says which screen of it. One line reading "Reception" leaves the bar looking
            like a page header rather than a dedicated display. */}
        <span className="reception-topbar__title">
          <span className="reception-topbar__eyebrow">{t("react_reception_title")}</span>
          <span className="reception-topbar__screen">{t("react_reception_live_dashboard")}</span>
        </span>
      </div>

      {/* The board polls rather than streaming (§9: "poll GET /board for now"), so this says the
          screen keeps itself current — it does NOT claim a live socket. The freshness stamp in
          the footer is the precise version of the same fact. */}
      <span className="reception-topbar__live">
        <span className="reception-topbar__live-dot" aria-hidden="true" />
        {t("react_reception_live")}
      </span>

      <div className="reception-topbar__right">
        <ReceptionSitePicker
          sites={sites}
          siteId={siteId}
          onSiteChange={onSiteChange}
          counts={counts}
          siteCounts={siteCounts}
          isLoadingSiteCounts={isLoadingSiteCounts}
          onOpen={onSitePickerOpen}
        />

        <div className="reception-topbar__clock">
          <span className="reception-topbar__time">{formatClockTime(now)}</span>
          <span className="reception-topbar__date">{formatClockDate(now)}</span>
        </div>

        {/* Offered only when the site has a PIN (§7b). With none set there is nothing that could
            unlock the screen, so locking it would strand the desk — disabled with the reason, so
            somebody can go and ask an admin to set one rather than wonder why the button does
            nothing. The PIN is an admin's to set: "a lock the person it guards can change is not
            one." */}
        <button
          type="button"
          className="reception-btn reception-btn--ghost"
          onClick={onLock}
          disabled={!canLock}
          title={canLock ? t("react_reception_lock") : t("react_reception_no_pin_set")}
        >
          {t("react_reception_lock")}
        </button>

        <ReceptionUserMenu siteName={site?.name} userName={userName} />
      </div>
    </header>
  );
};

export { getInitials };
export default ReceptionTopBar;
